import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  Languages,
  User,
  Mail,
  Lock,
  CreditCard,
  Zap,
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  Crown,
  Calendar,
  Receipt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const { user, refreshUser, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  
  // Profile state
  const [name, setName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  
  // Dialogs
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/auth");
    }
  }, [isAuthenticated, navigate]);
  
  // Load user data
  useEffect(() => {
    if (user) {
      setName(user.name || "");
    }
  }, [user]);
  
  // Load subscription data
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/subscription`, {
          withCredentials: true
        });
        setSubscription(response.data);
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setLoadingSubscription(false);
      }
    };
    
    if (isAuthenticated) {
      fetchSubscription();
    }
  }, [isAuthenticated]);
  
  // Save profile
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/auth/profile`,
        { name },
        { withCredentials: true }
      );
      await refreshUser();
      toast.success("Perfil atualizado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar perfil");
    } finally {
      setSavingProfile(false);
    }
  };
  
  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres");
      return;
    }
    
    setSavingPassword(true);
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success("Senha alterada com sucesso!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSavingPassword(false);
    }
  };
  
  // Cancel subscription
  const handleCancelSubscription = async () => {
    setCancelling(true);
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/cancel-subscription`,
        {},
        { withCredentials: true }
      );
      await refreshUser();
      setSubscription(prev => ({ ...prev, plan: "free", can_cancel: false }));
      toast.success("Assinatura cancelada com sucesso");
      setShowCancelDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cancelar assinatura");
    } finally {
      setCancelling(false);
    }
  };
  
  // Delete account
  const handleDeleteAccount = async () => {
    setDeleting(true);
    
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/account`, {
        withCredentials: true
      });
      toast.success("Conta excluída com sucesso");
      logout();
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir conta");
    } finally {
      setDeleting(false);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };
  
  // Get plan display info
  const getPlanInfo = () => {
    if (!subscription) return { name: "Carregando...", color: "text-zinc-400" };
    
    if (subscription.is_lifetime || subscription.is_vip) {
      return { name: "Lifetime VIP", color: "text-purple-400", icon: Crown };
    } else if (subscription.plan === "pro") {
      return { name: "PRO", color: "text-yellow-400", icon: Zap };
    } else {
      return { name: "Free", color: "text-zinc-400", icon: User };
    }
  };
  
  const planInfo = getPlanInfo();
  
  if (!isAuthenticated || !user) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar ao Dashboard</span>
          </Link>
          
          <Link to="/" className="flex items-center gap-2">
            <Languages className="w-6 h-6 text-white" />
            <span className="text-lg font-bold text-white font-['Outfit']">LCF Translator</span>
          </Link>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Outfit']">Configurações da Conta</h1>
          <p className="text-zinc-400 mt-2">Gerencie suas informações pessoais e assinatura</p>
        </div>
        
        {/* Profile Section */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5" />
              Perfil
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Suas informações pessoais
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="email"
                      type="email"
                      value={user.email || ""}
                      disabled
                      className="pl-10 bg-zinc-800 border-zinc-700 text-zinc-400"
                    />
                  </div>
                  <p className="text-xs text-zinc-500">O email não pode ser alterado</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-300">Nome</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Seu nome"
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={savingProfile}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  {savingProfile ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar Alterações
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Password Section - Only for email auth */}
        {user.auth_type === "email" && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Alterar Senha
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Atualize sua senha de acesso
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-zinc-300">Senha Atual</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Digite sua senha atual"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                  />
                </div>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-zinc-300">Nova Senha</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-zinc-300">Confirmar Nova Senha</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repita a nova senha"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      required
                    />
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={savingPassword}
                    className="bg-white text-black hover:bg-zinc-200"
                  >
                    {savingPassword ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    Alterar Senha
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
        
        {/* Subscription Section */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Assinatura
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Gerencie seu plano e histórico de pagamentos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {loadingSubscription ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            ) : (
              <>
                {/* Current Plan */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      subscription?.plan === "pro" || subscription?.is_lifetime 
                        ? "bg-yellow-500/20" 
                        : "bg-zinc-700"
                    }`}>
                      {planInfo.icon && <planInfo.icon className={`w-6 h-6 ${planInfo.color}`} />}
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">Plano Atual</p>
                      <p className={`text-xl font-bold ${planInfo.color}`}>
                        {planInfo.name}
                      </p>
                      {subscription?.plan_expires_at && (
                        <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                          <Calendar className="w-3 h-3" />
                          Expira em: {formatDate(subscription.plan_expires_at)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {subscription?.plan === "free" && (
                      <Link to="/pricing">
                        <Button className="bg-yellow-500 hover:bg-yellow-600 text-black">
                          <Zap className="w-4 h-4 mr-2" />
                          Upgrade PRO
                        </Button>
                      </Link>
                    )}
                    
                    {subscription?.plan === "pro" && !subscription?.is_lifetime && (
                      <>
                        <Link to="/pricing">
                          <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">
                            Renovar
                          </Button>
                        </Link>
                        {subscription?.can_cancel && (
                          <Button
                            variant="outline"
                            className="border-red-800 text-red-400 hover:bg-red-900/20"
                            onClick={() => setShowCancelDialog(true)}
                          >
                            Cancelar
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Payment History */}
                {subscription?.transactions && subscription.transactions.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Histórico de Pagamentos
                    </h3>
                    <div className="space-y-2">
                      {subscription.transactions.map((tx) => (
                        <div
                          key={tx.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800"
                        >
                          <div className="flex items-center gap-3">
                            {tx.status === "paid" ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                            <div>
                              <p className="text-sm text-white">
                                {tx.payment_method === "stripe" ? "Cartão" : "Solana"}
                              </p>
                              <p className="text-xs text-zinc-500">
                                {formatDate(tx.created_at)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-white">
                              ${tx.amount?.toFixed(2)} {tx.currency?.toUpperCase()}
                            </p>
                            <p className={`text-xs ${
                              tx.status === "paid" ? "text-green-400" : "text-red-400"
                            }`}>
                              {tx.status === "paid" ? "Pago" : "Falhou"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        
        {/* Danger Zone */}
        <Card className="bg-zinc-900/50 border-red-900/50">
          <CardHeader>
            <CardTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Zona de Perigo
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Ações irreversíveis para sua conta
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-lg bg-red-950/20 border border-red-900/30">
              <div>
                <p className="text-white font-medium">Excluir Conta</p>
                <p className="text-sm text-zinc-400">
                  Isso excluirá permanentemente sua conta e todos os dados associados.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-red-800 text-red-400 hover:bg-red-900/20"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
      
      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Cancelar Assinatura PRO</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Tem certeza que deseja cancelar sua assinatura PRO? Você perderá acesso a:
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-zinc-300 space-y-2 my-4">
            <li className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-400" />
              Tradução ilimitada
            </li>
            <li className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-400" />
              Segunda Janela de Tradução
            </li>
            <li className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-400" />
              Suporte prioritário
            </li>
          </ul>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              className="border-zinc-700 text-white"
            >
              Manter Assinatura
            </Button>
            <Button
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {cancelling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Excluir Conta Permanentemente</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Esta ação é irreversível. Todos os seus dados serão permanentemente excluídos, incluindo:
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-zinc-300 space-y-2 my-4">
            <li className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-400" />
              Informações do perfil
            </li>
            <li className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-400" />
              Histórico de uso
            </li>
            <li className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-400" />
              Dados de assinatura
            </li>
          </ul>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="border-zinc-700 text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Excluir Minha Conta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-16 py-6">
        <p className="text-center text-zinc-700 text-xs">
          {t("footerCopyright")}
        </p>
      </footer>
    </div>
  );
}
